'use client';

import DallasDialog from '@ui/Dallas/Dialogs/DallasDialog';
import DallasStack from '@ui/Dallas/Stack/DallasStack';
import DallasText from '@ui/Dallas/Text/DallasText';
import useFetchOrganizationInviteDetails from './useFetchOrganizationInviteDetails';
import DallasButton from '@ui/Dallas/Buttons/DallasButton/DallasButton';
import { useEffect, useState } from 'react';
import { openSnackbar } from '@ui/Dallas/Snackbar/DallasSnackbarService';
import DallasImageHandshake from '@ui/Dallas/Images/Handshake/DallasImageHandshake';
import { getInvalidSnackbarSubheaderText } from './copy';
import useRespondToOrganizationInvite from './useRespondToOrganizationInvite';
import DallasSnackbar from '@ui/Dallas/Snackbar/DallasSnackbar';

import styles from './OrganizationInviteDialog.module.scss';
import useOrganizationInviteSessionStorage from './useOrganizationInviteSessionStorage';

interface OrganizationInviteDialogContentProps {
  inviteId: string;
  secureToken: string;
}

function OrganizationInviteDialogContent({
  inviteId,
  secureToken,
}: OrganizationInviteDialogContentProps) {
  const [didShowInvalidSnackbar, setDidShowInvalidSnackbar] = useState(false);

  const { data: inviteDetails } = useFetchOrganizationInviteDetails(inviteId);

  const { accept, decline, didError, didSucceed } =
    useRespondToOrganizationInvite(secureToken);
  const didRespondToInvite = didError || didSucceed;

  const {
    didDismissModal,
    setDidDismissModal,
    reset: resetSessionStorage,
  } = useOrganizationInviteSessionStorage();

  useEffect(() => {
    if (!inviteDetails || didRespondToInvite) {
      return;
    }
    if (inviteDetails?.status !== 'pending' && !didShowInvalidSnackbar) {
      setDidShowInvalidSnackbar(true);
      openSnackbar({
        color: 'warning',
        header: 'Invite no longer valid',
        subheader: getInvalidSnackbarSubheaderText(inviteDetails),
      });
      resetSessionStorage();
    }
  }, [
    didRespondToInvite,
    didShowInvalidSnackbar,
    inviteDetails,
    resetSessionStorage,
  ]);

  if (
    didShowInvalidSnackbar ||
    didRespondToInvite ||
    !inviteDetails ||
    inviteDetails?.status !== 'pending'
  ) {
    return null;
  }

  if (didDismissModal) {
    return (
      <DallasStack
        alignHorizontal="center"
        className={styles.pendingSnackbarContainer}
      >
        <DallasSnackbar
          color="default"
          header={`You've been invited to join ${inviteDetails.inviter.carrierName}!`}
          subheader="Click to accept or decline."
          action={{
            label: 'Respond',
            onClick: () => setDidDismissModal(false),
          }}
        />
      </DallasStack>
    );
  }

  return (
    <DallasDialog
      className={styles.root}
      padding="3xl"
      onClose={() => setDidDismissModal(true)}
      content={
        <DallasStack alignHorizontal="center" flex={false}>
          <DallasStack
            className={styles.heroContainer}
            alignHorizontal="center"
            alignVertical="center"
          >
            <DallasImageHandshake
              className={styles.hero}
              width={120}
              height={120}
            />
          </DallasStack>
          <DallasText variant="headerBoldM">{"You're Invited!"}</DallasText>
          <DallasText align="center">
            <b>{inviteDetails.inviter.email} </b>
            invited you to join the {inviteDetails.inviter.carrierName} team on
            TruckSmarter. Accept the invite to get started.
          </DallasText>
          <DallasStack gap="md" alignHorizontal="center" direction="row">
            <DallasButton color="secondary" label="Decline" onClick={decline} />
            <DallasButton
              color="primary"
              label="Accept invite"
              className={styles.primaryButton}
              onClick={accept}
            />
          </DallasStack>
        </DallasStack>
      }
    />
  );
}

export default function OrganizationInviteDialog() {
  const { inviteId, secureToken } = useOrganizationInviteSessionStorage();

  if (!inviteId || !secureToken) {
    return null;
  }

  return (
    <OrganizationInviteDialogContent
      inviteId={inviteId}
      secureToken={secureToken}
    />
  );
}
